import { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaSave, FaCommentSlash, FaUserCheck, FaBan } from 'react-icons/fa';
import api from '../../api';

export default function ChatPermissions() {
  const [users, setUsers]       = useState([]);
  const [roles, setRoles]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const [editingUser, setEditingUser] = useState(null);
  const [restricted, setRestricted]   = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [allowedIds, setAllowedIds]   = useState(() => new Set());
  const [saving, setSaving]           = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.getUsers(), api.getRoles()])
      .then(([u, r]) => { setUsers(u); setRoles(r); })
      .catch(() => setError('Failed to load employees.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const chatEnabledRoles = new Set(roles.filter((r) => r.permissions.includes('chat_access')).map((r) => r.name));
  const chatEmployees = users.filter((u) => u.active && chatEnabledRoles.has(u.role));

  const openEdit = async (user) => {
    setEditingUser(user);
    setRestricted(user.chat_restricted);
    setChatEnabled(user.chat_enabled);
    setAllowedIds(new Set());
    setLoadingContacts(true);
    setError('');
    try {
      const data = await api.getUserChatContacts(user.id);
      setRestricted(data.restricted);
      setChatEnabled(data.chatEnabled);
      setAllowedIds(new Set(data.allowedUserIds));
    } catch (err) {
      setError(err.message || 'Failed to load chat contacts.');
    } finally {
      setLoadingContacts(false);
    }
  };

  const toggleAllowed = (id) => {
    setAllowedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!editingUser) return;
    setSaving(true);
    setError('');
    try {
      await api.updateUserChatContacts(editingUser.id, {
        restricted,
        chatEnabled,
        allowedUserIds: [...allowedIds],
      });
      setSuccess(`Chat permissions updated for ${editingUser.name}.`);
      setEditingUser(null);
      load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const otherEmployees = editingUser ? chatEmployees.filter((u) => u.id !== editingUser.id) : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-10">

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#0c1b4d]">Chat Permissions</h1>
          <p className="text-gray-500 text-sm mt-1">
            By default every employee can message any other chat-enabled employee. Restrict
            someone here to limit who they're allowed to start or continue chats with.
          </p>
        </div>

        {error   && <p className="text-red-600 text-sm mb-4 bg-red-50 px-4 py-2 rounded-lg">{error}</p>}
        {success && <p className="text-green-700 text-sm mb-4 bg-green-50 px-4 py-2 rounded-lg">{success}</p>}

        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-slate-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Name</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Title</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Chat Access</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {chatEmployees.map((u) => (
                    <tr key={u.id} className="border-b border-gray-50 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 whitespace-nowrap">
                        <p className="font-medium text-[#0c1b4d]">{u.name}</p>
                        <p className="text-xs text-gray-400">{u.employee_id || '—'}</p>
                      </td>
                      <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{u.title || '—'}</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {!u.chat_enabled ? (
                          <span className="flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700 w-fit">
                            <FaBan size={10} /> Messages Off
                          </span>
                        ) : u.chat_restricted ? (
                          <span className="flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 w-fit">
                            <FaCommentSlash size={10} /> Restricted
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700 w-fit">
                            <FaUserCheck size={10} /> Everyone
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => openEdit(u)}
                          className="px-3 py-1.5 text-xs font-semibold text-[#0c1b4d] border border-[#0c1b4d]/20 rounded-lg hover:bg-blue-50 transition-colors">
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                  {chatEmployees.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-6 text-center text-gray-400">No chat-enabled employees found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                <h2 className="font-bold text-[#0c1b4d]">{editingUser.name}'s Chat Permissions</h2>
                <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={chatEnabled}
                    onChange={(e) => setChatEnabled(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-blue-700"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-800">Enable Messages for this employee</span>
                    <span className="block text-xs text-gray-400 mt-0.5">
                      Unchecked: this employee loses access to Messages (and calling) entirely on
                      their next request, regardless of what their role normally grants.
                    </span>
                  </span>
                </label>

                <label className={`flex items-start gap-3 ${chatEnabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                  <input
                    type="checkbox"
                    checked={restricted}
                    disabled={!chatEnabled}
                    onChange={(e) => setRestricted(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-blue-700"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-800">Restrict who they can chat with</span>
                    <span className="block text-xs text-gray-400 mt-0.5">
                      Unchecked (default): can message any chat-enabled employee. Checked: can
                      only message the people selected below.
                    </span>
                  </span>
                </label>

                {chatEnabled && restricted && (
                  <div className="border border-gray-100 rounded-xl overflow-hidden">
                    <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-slate-50 border-b border-gray-100">
                      Allowed contacts
                    </p>
                    {loadingContacts ? (
                      <p className="px-4 py-3 text-sm text-gray-400">Loading...</p>
                    ) : otherEmployees.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-gray-400">No other chat-enabled employees found.</p>
                    ) : (
                      <div className="max-h-64 overflow-y-auto">
                        {otherEmployees.map((u) => (
                          <label key={u.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors cursor-pointer border-b border-gray-50 last:border-b-0">
                            <input
                              type="checkbox"
                              checked={allowedIds.has(u.id)}
                              onChange={() => toggleAllowed(u.id)}
                              className="w-4 h-4 accent-blue-700 flex-shrink-0"
                            />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-gray-800 truncate">
                                {u.name} {u.employee_id && <span className="text-gray-400 font-normal">({u.employee_id})</span>}
                              </span>
                              {u.title && <span className="block text-xs text-gray-500 truncate">{u.title}</span>}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
                <button onClick={handleSave} disabled={saving || loadingContacts}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-60">
                  <FaSave size={12} /> {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" onClick={() => setEditingUser(null)}
                  className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
